import { useAuthStore } from "@/stores/auth";

export interface BatchItemResult {
  id: string;
  ok: boolean;
  newId?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
}

export interface BatchResult {
  successCount: number;
  failedCount: number;
  items: BatchItemResult[];
}

export type BatchJobStatus = "QUEUED" | "RUNNING" | "SUCCEEDED" | "PARTIAL" | "FAILED";

export interface BatchAck {
  jobId: string;
  status: BatchJobStatus;
  total: number;
}

export interface BatchResponse {
  mode: "SYNC" | "ASYNC";
  result: BatchResult | null;
  job: BatchAck | null;
}

export type BatchLevelPath = "campaigns" | "adsets" | "ads";
export type BatchActionPath = "status" | "budget" | "copy" | "delete";

export interface BatchStatusBody { ids: string[]; status: "ACTIVE" | "PAUSED" }
export interface BatchBudgetBody { ids: string[]; dailyCents?: number | null; lifetimeCents?: number | null }
export interface BatchCopyBody {
  ids: string[];
  newStatus?: "ACTIVE" | "PAUSED" | "INHERITED_FROM_SOURCE" | null;
  /** ISO-8601 UTC，可空。Campaign / AdSet 支持，Ad 会被后端忽略。 */
  startTime?: string | null;
  endTime?: string | null;
}
export interface BatchDeleteBody { ids: string[] }

export type BatchBody = BatchStatusBody | BatchBudgetBody | BatchCopyBody | BatchDeleteBody;

async function authedFetch(input: string, init?: RequestInit): Promise<Response> {
  const token = useAuthStore.getState().accessToken;
  return fetch(input, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
  });
}

export async function batchAction(
  accountId: string,
  level: BatchLevelPath,
  action: BatchActionPath,
  body: BatchBody
): Promise<BatchResponse> {
  const r = await authedFetch(
    `/api/v1/ad-accounts/${accountId}/${level}/batch:${action}`,
    { method: "POST", body: JSON.stringify(body) }
  );
  if (!r.ok) {
    let data: { message?: string } = {};
    try { data = await r.json(); } catch { /* empty */ }
    throw new Error(data?.message || `请求失败 (${r.status})`);
  }
  return r.json();
}

export interface BatchJobView {
  id: string;
  adAccountId: string;
  level: "CAMPAIGN" | "ADSET" | "AD";
  action: "STATUS" | "BUDGET" | "COPY" | "DELETE";
  total: number;
  successCount: number;
  failedCount: number;
  status: BatchJobStatus;
  result: { items?: BatchItemResult[] } | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function getBatchJob(accountId: string, jobId: string): Promise<BatchJobView> {
  const r = await authedFetch(`/api/v1/ad-accounts/${accountId}/batch-jobs/${jobId}`);
  if (!r.ok) {
    let data: { message?: string } = {};
    try { data = await r.json(); } catch { /* empty */ }
    throw new Error(data?.message || `请求失败 (${r.status})`);
  }
  return r.json();
}
